import { useEffect, useRef, useState } from "react";
import { Film, ListVideo } from "lucide-react";
import { DAYS, HOUR_PX, fmtMin } from "./linearShared";

const etNow = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "numeric", hourCycle: "h23" }).formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday), min: Number(p.hour) * 60 + Number(p.minute) };
};
const snap = (px) => Math.round((px / HOUR_PX) * 4) * 15;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const endLabel = (m) => fmtMin(m).replace(" (end of day)", "");

const SlotBlock = ({ s, live, dragging, onPointerDown }) => {
  const h = ((s.end_min - s.start_min) / 60) * HOUR_PX;
  const Icon = s.content_kind === "playlist" ? ListVideo : Film;
  return (
    <div data-testid={`slot-${s.id}`} onPointerDown={(e) => onPointerDown(e, s, "move")}
      title={`${s.title}${s.church ? ` — ${s.church}` : ""} · ${fmtMin(s.start_min)}–${endLabel(s.end_min)}`}
      className={"absolute left-1 right-1 rounded-md border px-2 text-left overflow-hidden select-none touch-none group " + (h < 24 ? "py-0 leading-[16px] " : "py-1 ") +
        (dragging ? "z-30 shadow-2xl shadow-black/60 cursor-grabbing brightness-125 " : "cursor-grab hover:brightness-125 hover:z-20 ") +
        (s.content_kind === "playlist" ? "bg-sky-500/15 border-sky-500/40" : "bg-amber-500/15 border-amber-500/40") + (live ? " ring-2 ring-red-500/70" : "")}
      style={{ top: (s.start_min / 60) * HOUR_PX + 1, height: h - 2 }}>
      <div data-testid={`slot-resize-top-${s.id}`} onPointerDown={(e) => onPointerDown(e, s, "top")} className="absolute inset-x-0 top-0 h-1.5 cursor-ns-resize group-hover:bg-white/20" />
      <p className="text-[11px] font-semibold text-white truncate flex items-center gap-1"><Icon className="h-3 w-3 shrink-0" />{s.title}</p>
      {h > 30 && <p className="text-[10px] text-gray-400 truncate">{fmtMin(s.start_min)} – {endLabel(s.end_min)}</p>}
      {h > 46 && s.church && <p className="text-[10px] text-gray-500 truncate">{s.church}</p>}
      <div data-testid={`slot-resize-bottom-${s.id}`} onPointerDown={(e) => onPointerDown(e, s, "bottom")} className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize group-hover:bg-white/20" />
    </div>
  );
};

export const ScheduleGrid = ({ slots, onAdd, onEdit, onMove, nowSlotId }) => {
  const scroller = useRef(null);
  const drag = useRef(null);
  const [preview, setPreview] = useState(null);
  const now = etNow();
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = Math.max(0, (now.min / 60 - 1.5) * HOUR_PX); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const clickCol = (day, e) => {
    if (drag.current) return;
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const start = Math.min(1425, Math.floor((y / HOUR_PX) * 4) * 15);
    onAdd({ day, start_min: start, end_min: Math.min(1440, start + 60) });
  };

  const onPointerDown = (e, s, mode) => {
    if (e.button !== 0) return;
    e.stopPropagation(); e.preventDefault();
    const col = e.currentTarget.closest("[data-day-col]").getBoundingClientRect();
    const sc = scroller.current;
    drag.current = { s, mode, x: e.clientX, y: e.clientY, scroll: sc.scrollTop, colW: col.width, moved: false, next: s };
    const move = (ev) => {
      const d = drag.current;
      const box = sc.getBoundingClientRect();
      if (ev.clientY > box.bottom - 30) sc.scrollTop += 14; else if (ev.clientY < box.top + 30) sc.scrollTop -= 14;
      const rawDy = ev.clientY - d.y + (sc.scrollTop - d.scroll);
      const dy = snap(rawDy), dd = mode === "move" ? Math.round((ev.clientX - d.x) / d.colW) : 0;
      if (Math.abs(rawDy) > 4 || Math.abs(ev.clientX - d.x) > 4) d.moved = true;
      const len = s.end_min - s.start_min;
      let n;
      if (mode === "move") { const st = clamp(s.start_min + dy, 0, 1440 - len); n = { ...s, day: clamp(s.day + dd, 0, 6), start_min: st, end_min: st + len }; }
      else if (mode === "top") n = { ...s, start_min: clamp(s.start_min + dy, 0, s.end_min - 15) };
      else n = { ...s, end_min: clamp(s.end_min + dy, s.start_min + 15, 1440) };
      d.next = n; setPreview(n);
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      const d = drag.current;
      setPreview(null);
      const changed = d.next.day !== s.day || d.next.start_min !== s.start_min || d.next.end_min !== s.end_min;
      if (ev.type !== "pointercancel") { if (!d.moved) onEdit(s); else if (changed) onMove(s, d.next); }
      setTimeout(() => { drag.current = null; }, 0);
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };

  const shown = slots.map((s) => (preview && preview.id === s.id ? preview : s));
  return (
    <div data-testid="linear-grid" className="rounded-2xl border border-gray-800 bg-card overflow-hidden">
      <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-gray-800 bg-black/20">
        <div />
        {DAYS.map((d, i) => <div key={d} className={"px-2 py-2.5 text-xs font-semibold text-center " + (i === now.day ? "text-amber-300" : "text-gray-400")}>{d.slice(0, 3)}</div>)}
      </div>
      <div ref={scroller} className="max-h-[620px] overflow-y-auto">
        <div className="grid grid-cols-[64px_repeat(7,1fr)] relative" style={{ height: 24 * HOUR_PX }}>
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => <span key={h} className="absolute right-2 text-[10px] text-gray-500 -translate-y-1/2" style={{ top: h * HOUR_PX }}>{h ? fmtMin(h * 60).replace(":00", "") : ""}</span>)}
          </div>
          {DAYS.map((d, day) => (
            <div key={d} data-day-col data-testid={`grid-day-${day}`} onClick={(e) => clickCol(day, e)} className="relative border-l border-gray-800/70 cursor-copy hover:bg-white/[0.015]"
              style={{ backgroundImage: `repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0 1px, transparent 1px ${HOUR_PX}px), repeating-linear-gradient(to bottom, rgba(255,255,255,0.025) 0 1px, transparent 1px ${HOUR_PX / 4}px)` }}>
              {day === now.day && <div className="absolute left-0 right-0 h-px bg-red-500 z-10 pointer-events-none" style={{ top: (now.min / 60) * HOUR_PX }}><span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" /></div>}
              {shown.filter((s) => s.day === day).map((s) => <SlotBlock key={s.id} s={s} live={s.id === nowSlotId} dragging={preview?.id === s.id} onPointerDown={onPointerDown} />)}
            </div>
          ))}
        </div>
      </div>
      <p className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-800">Drag a slot to move it, drag its top or bottom edge to change times, or click it to edit.</p>
    </div>
  );
};
